import { useChartDefinitionsContext } from '@/components/ChartDefinitionsContext';
import TimeSeries, { Key, Line, Range } from '@/components/TimeSeries';
import { Now } from '@/components/TimeSeries/components/Markers/styled';
import { useTimeSeriesReducer } from '@/components/TimeSeries/hooks';
import {
  formatterCurrencyTruncated,
  formatterPercentage,
} from '@/utilities/format';
import { toCurrencyTruncated } from '@/utilities/formatting';
import { getTimeSeriesPeriodMap } from '@/utilities/timeSeriesPeriodMap';
import { Text, useTheme } from '@simplywallst/ui-core';
import { addYears, format, subDays, subYears } from 'date-fns';
import { useEffect, useMemo } from 'react';
import { useValuationTranslation } from '@/pages/CompanyReport/components/Valuation/hooks/useValuationTranslation';
import {
  daysBack,
  dispersionThreshold,
  growthThreshold,
  minimumPriceTargets,
} from '../../constants';
import type { PriceTargetData } from '../../queryPriceTargets';
import {
  getGrowthColor,
  getPriceGrowthData,
  getValidPrice,
  isPositiveMarker,
} from '../../utils';
import { Dot, StyledTd, Table } from './styled';

import { VisxText } from '@/components/VisxText';
import { DEFAULT_FILTER, Series, filterSeries, findMinMax } from './utils';

interface props {
  currencyISO: string;
  useNewCalcFlag: boolean;
  priceTargetData: number[][];
  priceTargetKeys: number[];
  analystData: PriceTargetData['analystData'];
  priceTargetMax: number;
  priceTargetMin: number;
  priceTargetRangeHigh: number[][];
  priceTargetRangeLow: number[][];
  priceData: number[][];
  priceMax: number;
  priceMin: number;
  dayZero: number;
  isDataTrustworthy?: boolean | null;
}
export const AnalystsPriceTargetsChart = ({
  currencyISO,
  useNewCalcFlag,
  priceTargetData,
  priceTargetKeys,
  analystData,
  priceTargetMax,
  priceTargetMin,
  priceTargetRangeHigh,
  priceTargetRangeLow,
  priceData,
  priceMax,
  priceMin,
  dayZero,
  isDataTrustworthy,
}: props) => {
  const theme = useTheme();
  const { chart05Gradient02, chartNEGGradient02, chartPOSGradient02 } =
    useChartDefinitionsContext();

  const periodMap = useMemo(() => getTimeSeriesPeriodMap(), []);
  const [targetData, targetKeys, analystMetaData] = useMemo(() => {
    const date = new Date(periodMap['2Y']);
    const month = date.getMonth();
    const year = date.getFullYear();
    const utcDate = Date.UTC(year, month, 1);
    const startDate = subDays(utcDate, 1).getTime();
    const endDate = addYears(utcDate, 3).getTime();
    const targetPriceDataDefault = [
      [startDate, -1],
      [endDate, -1],
    ];
    const targetPriceKeysDefault = [startDate, endDate];
    const analystDataDefault = {
      [startDate]: { dispersion: 0, analystCount: 0 },
      [endDate]: { dispersion: 0, analystCount: 0 },
    };
    //Return default values when analyst data is not available to render chart's default state
    if (
      typeof isDataTrustworthy !== 'boolean' ||
      priceTargetData.length < minimumPriceTargets
    )
      return [
        targetPriceDataDefault,
        targetPriceKeysDefault,
        analystDataDefault,
      ];
    return [priceTargetData, priceTargetKeys, analystData];
  }, [
    priceTargetData,
    priceTargetKeys,
    analystData,
    periodMap,
    isDataTrustworthy,
  ]);

  const seriesData = useMemo(
    () => ({
      [Series.series01]: priceData,
      [Series.series02]: targetData,
      [Series.rangeTargetHigh]: priceTargetRangeHigh,
      [Series.rangeTargetLow]: priceTargetRangeLow,
    }),
    [priceData, targetData, priceTargetRangeHigh, priceTargetRangeLow]
  );
  const colors = {
    [Series.series01]: {
      solid: theme.color.chart01,
      opaque: theme.color.chart01RGBA(0.5),
    },
    [Series.series02]: {
      solid: theme.color.chart05,
      opaque: chart05Gradient02,
    },
  };

  const t = useValuationTranslation();

  const seriesLabel = {
    [Series.series01]: t('analystPriceTarget.chart.sharePrice'),
    [Series.series02]: t('analystPriceTarget.chart.average'),
  };

  const {
    entities,
    keys,
    minimumX,
    maximumX,
    periods,
    series,
    filter,
    disabled,
    dispatch,
  } = useTimeSeriesReducer<Series>({
    data: seriesData,
    dayZero,
    defaultFilter: DEFAULT_FILTER,
    hideToggleFilter: false,
  });
  useEffect(() => {
    dispatch({
      type: 'reset',
      payload: {
        data: seriesData,
        dayZero,
        defaultFilter: DEFAULT_FILTER,
      },
    });
  }, [dispatch, seriesData, dayZero]);

  const filteredSeries = useMemo(
    () => series.filter((n) => filterSeries(n) && filter[n]),
    [series, filter]
  );

  const [minimumY, maximumY] = useMemo(
    () =>
      findMinMax(filteredSeries, {
        priceMin,
        priceMax,
        priceTargetMin,
        priceTargetMax,
      }),
    [filteredSeries, priceMin, priceMax, priceTargetMin, priceTargetMax]
  );

  return (
    <TimeSeries
      keys={targetKeys}
      filter={filter}
      disabled={disabled}
      series={series}
      periods={periods}
      entities={entities}
      minimumX={minimumX}
      maximumX={maximumX}
      minimumY={minimumY}
      maximumY={maximumY}
      moveWithTrailingYear
      pastLabel="Past"
      futureLabel="12m Forecast"
      formatYAxis={(value) => toCurrencyTruncated(value, 2, currencyISO)}
      dataTable={({ key: date }) => {
        if (typeof date !== 'number' || entities[date] === undefined)
          return <></>;
        const oneYearAgo = subYears(date, 1).getTime();
        const price = getValidPrice(oneYearAgo, priceData, daysBack);
        const priceTarget = entities[date].priceTarget;
        const dispersion = analystMetaData[date].dispersion;
        const { growth, growthSign } = getPriceGrowthData(
          price,
          priceTarget,
          useNewCalcFlag
        );
        return (
          <Table data-cy-id="analyst-price-targets-tooltip">
            <thead>
              <tr>
                <th colSpan={2}>
                  {oneYearAgo ? format(oneYearAgo, 'MMM dd yyyy') : `n/a`}
                </th>
                <th>
                  {t('analystPriceTarget.chart.heading', {
                    count: analystMetaData[date].analystCount ?? undefined,
                  })}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{t('analystPriceTarget.table.header.sharePrice')}</td>
                <StyledTd color={theme.color.brand01} colSpan={2}>
                  {formatterCurrencyTruncated(price, currencyISO)}
                </StyledTd>
              </tr>
              <tr>
                <td>{t('analystPriceTarget.table.header.average')}</td>
                {priceTarget >= 0 ? (
                  <StyledTd color={theme.color.chart05}>
                    {formatterCurrencyTruncated(priceTarget, currencyISO)}
                  </StyledTd>
                ) : (
                  <td>{t('analystPriceTarget.chart.notApplicable')}</td>
                )}
                <StyledTd color={getGrowthColor(growth, theme)}>
                  {price !== null &&
                    priceTarget >= 0 &&
                    growthSign + formatterPercentage(growth)}
                </StyledTd>
              </tr>
              <tr>
                <td>{t('analystPriceTarget.chart.agreement')}</td>
                <td colSpan={2}>
                  {priceTarget >= 0 ? (
                    <>
                      <Text
                        typography="tiny"
                        color={
                          dispersion <= dispersionThreshold ? 'good' : 'bad'
                        }
                      >
                        {dispersion <= dispersionThreshold
                          ? t('analystPriceTarget.chart.dispersion.good')
                          : t('analystPriceTarget.chart.dispersion.low')}
                      </Text>
                      <Text
                        typography="tiny"
                        color="whiteRGBA"
                        opacity={0.5}
                        // This is a temporary measure to keep the visual layout of the tooltip together for any arbitrary
                        // text length (translations for these descriptions are often more than 2 lines). Without this fix,
                        // the table causes the section layout to shift as users are hovering on the chart.
                        //
                        // FIXME: ensure the table can handle different responsive sizes of text content _without_ causing
                        // layout shift.
                        style={{
                          maxHeight: '36px',
                          overflow: 'hidden',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {dispersion <= dispersionThreshold
                          ? t('analystPriceTarget.chart.analystsAgreement.less')
                          : t(
                              'analystPriceTarget.chart.analystsAgreement.more'
                            )}
                      </Text>
                    </>
                  ) : (
                    <Text typography="tiny" color="whiteRGBA" opacity={0.7}>
                      {t('analystPriceTarget.chart.notApplicable')}
                    </Text>
                  )}
                </td>
              </tr>
            </tbody>
          </Table>
        );
      }}
      nowMarker={({ x, height, hover }) => (
        <Now
          y="0"
          x={x}
          width="1"
          height={24 + height}
          fill={colors[Series.series02].solid}
          hover={hover}
        />
      )}
      marker={({ key: date, xScale, yScale }) => {
        if (typeof date !== 'number' || entities[date] === undefined)
          return <></>;
        const oneYearAgo = subYears(date, 1).getTime();
        const validPrice = getValidPrice(oneYearAgo, priceData, daysBack);
        const priceTarget = entities[date].priceTarget;
        if (validPrice === null || priceTarget < 0) return <></>;
        const xCoordTargetPrice = xScale(date);
        const yCoordTargetPrice = yScale(priceTarget);
        const xCoordPrice = xScale(oneYearAgo);
        const yCoordPrice = yScale(validPrice);
        const yCoordTargetHigh = yScale(entities[date].priceTargetRangeHigh);
        const yCoordTargetLow = yScale(entities[date].priceTargetRangeLow);
        const { growth } = getPriceGrowthData(
          validPrice,
          priceTarget,
          useNewCalcFlag
        );
        const isPositiveValue = isPositiveMarker(
          analystMetaData[date].dispersion,
          dispersionThreshold,
          growth,
          growthThreshold,
          useNewCalcFlag
        );
        const beamCoords = `${xCoordPrice} ${yCoordPrice}, ${xCoordTargetPrice} ${yCoordTargetHigh}, ${xCoordTargetPrice}, ${yCoordTargetLow}`;
        return (
          <>
            <line
              x1={xCoordPrice}
              y1={yCoordPrice}
              x2={xCoordTargetPrice}
              y2={yCoordTargetPrice}
              stroke={isPositiveValue ? theme.color.good : theme.color.bad}
              strokeWidth={2}
              strokeDasharray="5, 5"
            />
            <circle
              key={`data-point-target-${series}`}
              transform={`translate(${xCoordTargetPrice},${yCoordTargetPrice})`}
              r="4"
              fill={colors[Series.series02].solid}
              stroke="white"
              strokeWidth="1"
            />
            <circle
              key={`data-point-price-${series}`}
              transform={`translate(${xCoordPrice},${yCoordPrice})`}
              r="4"
              fill={colors[Series.series01].solid}
              stroke="white"
              strokeWidth="1"
            />
            <polygon
              points={beamCoords}
              overflow="visible"
              fill={isPositiveValue ? chartPOSGradient02 : chartNEGGradient02}
            />
          </>
        );
      }}
      legend={() =>
        filteredSeries.map((series) => {
          return (
            <Key key={`timeseries-key-${series}`} active={filter[series]}>
              <Dot
                color={
                  colors[series].solid ? colors[series].solid : colors[series]
                }
                active={filter[series]}
              />
              <span>{seriesLabel[series]}</span>
            </Key>
          );
        })
      }
    >
      {({ keys: dates, xScale, yScale, entities, chartWidth, chartHeight }) =>
        filteredSeries.map((key) => {
          if (entities[dates[1]] === undefined) return <></>;
          const priceTarget = entities[dates[1]].priceTarget;
          const todayCoord = xScale(periodMap.NOW);
          const nextYearCoord = xScale(dates[1]);
          const x =
            todayCoord && nextYearCoord
              ? (todayCoord + nextYearCoord) / 2
              : chartWidth - 50;
          const y = chartHeight / 2 + 24;
          return (
            <g key={`chart-${key}`}>
              {key === Series.series02 && priceTarget >= 0 && (
                <Range
                  data={keys.filter(
                    (d) =>
                      entities[d][Series.rangeTargetHigh] !== undefined &&
                      entities[d][Series.rangeTargetLow] !== undefined
                  )}
                  xSelect={(d) => d}
                  ySelectHigh={(d) => entities[d][Series.rangeTargetHigh]}
                  ySelectLow={(d) => entities[d][Series.rangeTargetLow]}
                  xScale={xScale}
                  yScale={yScale}
                  fillPos={colors[Series.series02].opaque}
                  fillNeg={colors[Series.series02].opaque}
                />
              )}
              {filterSeries(key) && (
                <Line
                  data={keys.filter((d) => entities[d][key] !== undefined)}
                  xSelect={(d) => d}
                  ySelect={(d) => (entities[d] ? entities[d][key] : 0)}
                  xScale={xScale}
                  yScale={yScale}
                  strokePos={
                    key === Series.series01
                      ? colors[key].opaque
                      : colors[key].solid
                  }
                  strokeNeg={theme.color.bad}
                />
              )}
              {priceTarget < 0 && (
                <VisxText
                  x={x}
                  y={y}
                  verticalAnchor={'start'}
                  textAnchor={'middle'}
                  fill={theme.color.white}
                  typography="tiny"
                >
                  {t('analystPriceTarget.chart.noData')}
                </VisxText>
              )}
            </g>
          );
        })
      }
    </TimeSeries>
  );
};
